import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { jwtDecode } from "jwt-decode";
import AOS from "aos";
import "aos/dist/aos.css";
import BulkProductImport from "./BulkProductImport";
import AddOneProduct from "./AddOneProduct";
import Pagination from "./PaginationComp";
import Select from "react-select";
import api from "../utils/api";
import Papa from "papaparse";
import { DownloadCloud } from "lucide-react";

// ── Veg / Non-Veg badge ────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const isVeg = type?.toLowerCase() === "veg";
  const isNonVeg = type?.toLowerCase() === "non-veg";
  if (!isVeg && !isNonVeg) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
      isVeg ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isVeg ? "bg-green-500" : "bg-red-500"}`} />
      {isVeg ? "Veg" : "Non-Veg"}
    </span>
  );
};

// ── Stock indicator ────────────────────────────────────────────────────────
const StockBadge = ({ stock }) => {
  const n = Number(stock) || 0;
  if (n === 0) return <span className="text-xs text-red-500 font-semibold">Out of stock</span>;
  if (n < 10) return <span className="text-xs text-amber-600 font-semibold">{n} (Low)</span>;
  return <span className="text-xs text-green-700 font-semibold">{n}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
const AddProduct = () => {
  const [data, setData] = useState([]);
  const [userId, setUserId] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
  const [productToUpdate, setProductToUpdate] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isUpdatingPolicy, setIsUpdatingPolicy] = useState(false);
  const [globalStockState, setGlobalStockState] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    if (data.length > 0) {
      setGlobalStockState(data.every(i => i.allowOutOfStockOrders));
    }
  }, [data]);

  useEffect(() => {
    AOS.init({ duration: 600, once: true });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      if (decoded.user) setUserId(decoded.user.id);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = async (page = currentPage) => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`get-products/${userId}?page=${page}&limit=10&search=${debouncedSearch}&category=${selectedCategory}`);
      setData(res.data.data || []);
      setPagination(res.data.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 });
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!userId) return;
    try {
      const response = await api.get(`/newcategories/${userId}?limit=1000`);
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchCategories();
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchData(currentPage);
  }, [userId, currentPage, debouncedSearch, selectedCategory]);

  const handleUpdateProduct = (item) => { setProductToUpdate(item); setShowProductModal(true); };
  const handleAddProduct = () => { setProductToUpdate(null); setShowProductModal(true); };

  const handleToggleAvailability = async (item) => {
    try {
      await api.put(`product/${item._id}`, {
        isAvailable: !item.isAvailable
      });
      fetchData(currentPage);
      toast.success(`Product ${!item.isAvailable ? 'marked as available' : 'hidden from orders'}`);
    } catch (error) {
      console.error("Error toggling availability:", error);
      toast.error("Failed to update availability");
    }
  };

  const handleProductSuccess = (action, message) => {
    if (action === "add") toast.success("✅ Product added successfully!");
    else if (action === "update") toast.success("✅ Product updated successfully!");
    else if (action === "error") toast.error(message || "Operation failed!");
    setShowProductModal(false);
    fetchData(currentPage);
  };

  const handleBulkPolicyUpdate = async (status) => {
    try {
      setIsUpdatingPolicy(true);
      const res = await api.put(`/bulk-out-of-stock-policy/${userId}`, { status });
      if (res.data.success) {
        toast.success(`✅ Global policy updated: Out-of-stock orders ${status ? 'Allowed' : 'Blocked'}`);
        fetchData(currentPage);
      }
    } catch (err) {
      console.error(err);
      toast.error("❌ Failed to update global policy.");
    } finally {
      setIsUpdatingPolicy(false);
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Delete Product?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`product/${id}`);
          toast.success("Product deleted!");
          fetchData(currentPage);
        } catch (error) {
          toast.error(error.response?.data?.message || "Error deleting product");
        }
      }
    });
  };

  const handleExportProducts = async () => {
    try {
      const res = await api.get(`get-products/${userId}?limit=1000000&search=${debouncedSearch}&category=${selectedCategory}`);
      const allProducts = res.data.data || [];
      
      if (allProducts.length === 0) {
        toast.info("No products to export.");
        return;
      }

      const csvData = allProducts.map(p => {
        const variantsStr = (p.variants || []).map(v => `${v.name}:${v.price}`).join(" | ");
        const addOnsStr = (p.addOns || []).map(a => `${a.name}:${a.price}`).join(" | ");
        
        return {
          productName: p.productName || "",
          category: p.category?.newTitle || p.category || "",
          price: p.price || 0,
          type: p.type || "Veg",
          unit: p.unit || "pieces",
          stock: p.stock || 0,
          description: p.description || "",
          image: p.image || "",
          variants: variantsStr,
          addOns: addOnsStr
        };
      });

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Products exported successfully!");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export products.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <ToastContainer position="top-right" />

      {/* ── Page Header ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        data-aos="fade-down">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🍽️ Products</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage your menu items — {pagination.total} product{pagination.total !== 1 ? "s" : ""} total
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={handleExportProducts}
            className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-xl shadow-sm hover:shadow transition-all text-sm"
          >
            <DownloadCloud className="w-4 h-4 text-slate-500" />
            Export Products
          </button>
          <BulkProductImport userId={userId} onImportComplete={() => {}} refreshProducts={() => fetchData(1)} />
          <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1.5 shadow-inner border border-gray-200 px-3" title="Toggle to allow or block out-of-stock orders for all products">
            <div className="flex flex-col text-right mr-1">
              <span className="text-xs font-bold text-gray-700 leading-tight">Out-of-Stock Orders</span>
              <span className="text-[9px] text-gray-500 font-medium tracking-wide uppercase">Global Policy</span>
            </div>
            
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={globalStockState}
                disabled={isUpdatingPolicy}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const msg = checked ? "Allow ordering out-of-stock items for ALL products?" : "Block ordering out-of-stock items for ALL products?";
                  if (window.confirm(msg)) {
                    setGlobalStockState(checked);
                    handleBulkPolicyUpdate(checked);
                  }
                }}
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500 opacity-90 hover:opacity-100"></div>
              <span className="absolute left-1 top-1.5 text-[10px] pointer-events-none z-10 transition-opacity">
                {isUpdatingPolicy ? "⏳" : (!globalStockState ? "🔒" : "")}
              </span>
              <span className="absolute right-1.5 top-1.5 text-[10px] pointer-events-none z-10 transition-opacity">
                {isUpdatingPolicy ? "" : (globalStockState ? "🔓" : "")}
              </span>
            </label>
          </div>
          <button
            id="add-product-btn"
            onClick={handleAddProduct}
            className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
          >
            <span className="text-lg leading-none">+</span>
            Add Product
          </button>
        </div>
      </div>

      {/* ── Search Bar ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 mb-5 flex flex-col sm:flex-row items-center gap-3" data-aos="fade-up">
        <div className="flex flex-1 w-full items-center gap-3 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100">
          <span className="text-gray-400 text-lg">🔍</span>
          <input
            type="text"
            placeholder="Search by product name, category..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            className="flex-1 text-sm text-gray-700 bg-transparent focus:outline-none placeholder-gray-400"
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
          )}
        </div>
        
        <div className="flex w-full sm:w-64 items-center gap-2">
          <Select
            options={[
              { value: "all", label: "All Categories" },
              ...categories.map((cat) => ({ value: cat._id, label: cat.newTitle })),
            ]}
            value={
              selectedCategory === "all"
                ? { value: "all", label: "All Categories" }
                : categories
                    .filter((cat) => cat._id === selectedCategory)
                    .map((cat) => ({ value: cat._id, label: cat.newTitle }))[0]
            }
            onChange={(selectedOption) => {
              setSelectedCategory(selectedOption.value);
              setCurrentPage(1);
            }}
            placeholder="Search Categories..."
            isSearchable
            className="w-full text-sm"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </div>

      {/* ── Product Table ───────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-aos="fade-up">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">#</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Product</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Category</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Type</th>
                <th className="px-4 py-3.5 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide">Price</th>
                <th className="px-4 py-3.5 text-center font-semibold text-gray-600 text-xs uppercase tracking-wide">Stock</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Variants</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Add-ons</th>
                <th className="px-4 py-3.5 text-center font-semibold text-gray-600 text-xs uppercase tracking-wide">Status</th>
                <th className="px-4 py-3.5 text-center font-semibold text-gray-600 text-xs uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                      <p className="text-gray-400 text-sm">Loading products...</p>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="9" className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <span className="text-5xl">🍽️</span>
                      <p className="text-sm">No products found{search ? ` for "${search}"` : ""}.</p>
                      <button onClick={handleAddProduct}
                        className="text-sm text-teal-600 hover:text-teal-800 font-semibold underline underline-offset-2">
                        Add your first product →
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((item, index) => (
                  <tr key={item._id} className="border-b border-gray-100 hover:bg-teal-50/40 transition-colors">
                    <td className="px-4 py-3.5 text-gray-500 font-mono text-xs">
                      {(pagination.page - 1) * pagination.limit + index + 1}
                    </td>

                    {/* Product Name + Image */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        {item.image || item.category?.image ? (
                          <div className="relative shrink-0">
                            <img src={item.image || item.category?.image} alt={item.productName}
                              className="w-9 h-9 rounded-lg object-cover border border-gray-200" />
                            {!item.image && item.category?.image && (
                              <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center" title="Category Default Image">
                                <span className="text-[8px] text-white font-bold">C</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center shrink-0 text-teal-600 font-bold text-sm">
                            {(item.productName || "?")[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">{item.productName || "—"}</p>
                          {item.description && (
                            <p className="text-xs text-gray-400 truncate max-w-[160px]">{item.description}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full font-medium">
                        {item.category?.newTitle || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-3.5">
                      <TypeBadge type={item.type} />
                    </td>

                    <td className="px-4 py-3.5 text-right">
                      <span className="font-bold text-gray-800 text-sm">₹{item.price || 0}</span>
                      {item.unit && <span className="text-xs text-gray-400 ml-1">/{item.unit}</span>}
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      <StockBadge stock={item.stock} />
                    </td>

                    <td className="px-4 py-3.5">
                      {item.variants?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.variants.slice(0, 2).map((v, i) => (
                            <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                              {v.name} ₹{v.price}
                            </span>
                          ))}
                          {item.variants.length > 2 && (
                            <span className="text-xs text-blue-500">+{item.variants.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3.5">
                      {item.addOns?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.addOns.slice(0, 2).map((a, i) => (
                            <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                              {a.name}
                            </span>
                          ))}
                          {item.addOns.length > 2 && (
                            <span className="text-xs text-amber-500">+{item.addOns.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      <label className="inline-flex items-center cursor-pointer" title={item.isAvailable ? "Available" : "Hidden"}>
                        <div className="relative">
                          <input type="checkbox" className="sr-only" checked={item.isAvailable} onChange={() => handleToggleAvailability(item)} />
                          <div className={`block w-10 h-6 rounded-full transition-colors ${item.isAvailable ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                          <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${item.isAvailable ? 'transform translate-x-4' : ''}`}></div>
                        </div>
                      </label>
                    </td>

                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleUpdateProduct(item)}
                          title="Edit Product"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          title="Delete Product"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ────────────────────────────────────────────── */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-4 border-t border-gray-100">
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.page}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>

      {/* ── Add/Edit Product Modal ─────────────────────────────────── */}
      {showProductModal && (
        <AddOneProduct
          userId={userId}
          refreshProducts={() => fetchData(currentPage)}
          onSuccess={handleProductSuccess}
          existingData={productToUpdate}
          onClose={() => setShowProductModal(false)}
        />
      )}
    </div>
  );
};

export default AddProduct;
